import { ColumnWidthOutlined } from '@ant-design/icons';
import {
  ConfigFormItem,
  IWidgetComponentProps,
  RichTextAdvancedRender,
  getWidgetProps,
} from '@framework/engine';
import { useRelationEditor } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { DividerTitleElement } from '@framework/ui';
import { Button, Flex, Tooltip, message } from 'antd';
import { Feature, Map, View } from 'ol';
import { GeoJSON } from 'ol/format';
import 'ol/ol.css';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { Subject } from 'rxjs';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { v4 } from 'uuid';
import { OpenLayersMap } from '../../../maps/OpenLayerMap';
import { IMapWidget } from './IMapWidget';
import { MapCadastralAreasTable } from './components/MapCadastralAreasTable';
import { MapGeometriesTable } from './components/MapGeometriesTable';
import {
  MapCadastralAreaItem,
  MapCadastralSuffix,
  MapGeometryItem,
} from './createMapSchema';
import { MapDrawingControls } from './drawing/MapDrawingControls';
import { MapSearchAddressControl } from './drawing/MapSearchAddressControl';
import { MapSearchCadastralControl } from './drawing/MapSearchCadastralControl';
import { MapUploadGeometriesControl } from './drawing/file/MapUploadGeometriesControl';
import { getRoundedFeatureArea } from './drawing/intersection/getRoundedFeatureArea';
import { useMapIntersectionManager } from './drawing/intersection/useMapIntersectionManager';
import { useMapDrawingInteractions } from './drawing/useMapDrawingInteractions';
import { MapLayersToggleControl } from './layers/controls/MapLayersToggleControl';
import { creteMapLayerFromConfig } from './layers/rendering/createMapLayerFromConfig';

export interface IMapWidgetComponentProps
  extends IWidgetComponentProps<IMapWidget> {}

const geoJSON = new GeoJSON({ featureProjection: 'EPSG:3857' });

export type MapSubjectEvent =
  | { type: 'add'; feature: Feature; addToSource?: boolean }
  | { type: 'upload'; features: Feature[] }
  | { type: 'modify'; feature: Feature }
  | { type: 'remove'; feature: Feature }
  | { type: 'removeId'; id: string };

export function MapWidgetComponent(props: IMapWidgetComponentProps) {
  const { widget } = props;
  const options = getWidgetProps(props);
  const { t } = useTranslation();
  const editor = useRelationEditor<MapGeometryItem>(options.name);
  const cadastralEditor = useRelationEditor<MapCadastralAreaItem>(
    `${options.name}${MapCadastralSuffix}`,
  );

  const mapRef = React.useRef<Map | null>(null);

  const { featuresSource, mode, handleModeChange, initDrawing, interactions } =
    useMapDrawingInteractions({
      widget,
      geometriesEditor: editor,
    });

  const { initIntersectionManager, triggerIntersections } =
    useMapIntersectionManager({
      featuresSource,
      editor: cadastralEditor,
      widget,
    });

  const mapSubject = React.useRef<Subject<MapSubjectEvent>>(new Subject());

  /**
   * Gestisce l'aggiunta, la modifica e la rimozione delle geometrie
   * disegnate sulla mappa.
   */
  useEffect(() => {
    const encodeFeature = (feature: Feature): MapGeometryItem => ({
      feature: geoJSON.writeFeature(feature),
      id: feature.getId() as string,
      area: getRoundedFeatureArea(feature),
    });

    const listener = mapSubject.current.subscribe(event => {
      console.log(`[MapWidgetComponent] mapSubject`, event);
      switch (event?.type) {
        case 'add': {
          const feature = event.feature;
          feature.setId(v4());
          if (!feature.getGeometry()) return;

          // Utilizzato per le particelle catatsali
          if (event.addToSource) {
            featuresSource.current.addFeature(feature);
          }

          // Per la selezione singola, rimuovo tutte le geometrie tranne quella
          // selezionata attualmente.
          if (widget.selection?.multiple === false) {
            featuresSource.current.removeFeatures(
              featuresSource.current.getFeatures().filter(f => f !== feature),
            );
            editor.replace([encodeFeature(feature)]);
          } else {
            editor.append(encodeFeature(feature));
          }

          triggerIntersections(feature);
          return;
        }

        case 'upload': {
          const features = event.features;
          if (features.length === 0) {
            message.warning(
              t(
                'map-widget.upload-warning-message',
                'Nessuna geometria presente nel file.',
              ),
            );
            return;
          }

          const items = features.map(feature => {
            feature.setId(v4());
            return encodeFeature(feature);
          });

          // Essendo un caricamento da file, non sono presenti ancora
          // nel layer delle geometrie disegnate
          featuresSource.current.addFeatures(features);

          editor.replace(items);
          triggerIntersections();

          mapRef.current?.getView().fit(featuresSource.current.getExtent(), {
            padding: [64, 64, 64, 64],
            duration: 400,
          });
          return;
        }

        case 'modify': {
          const feature = event.feature;
          const editorIndex = editor
            .getValue()
            .findIndex(i => i.id === feature.getId());
          if (editorIndex === -1) {
            console.warn(`[MapWidgetComponent] Feature non trovata nell'editor`, { feature, id: feature.getId() }); // prettier-ignore
            return;
          }

          editor.updateAt(editorIndex, encodeFeature(feature));
          triggerIntersections(feature);
          return;
        }

        case 'remove': {
          const feature = event.feature;
          const editorIndex = editor
            .getValue()
            .findIndex(i => i.id === feature.getId());
          if (editorIndex === -1) {
            console.warn(`[MapWidgetComponent] Feature non trovata nell'editor`, { feature, id: feature.getId() }); // prettier-ignore
            return;
          }

          featuresSource.current.removeFeature(feature);
          editor.removeAt(editorIndex);
          triggerIntersections();
          return;
        }

        case 'removeId': {
          const editorIndex = editor
            .getValue()
            .findIndex(i => i.id === event.id);
          if (editorIndex === -1) {
            console.warn(`[MapWidgetComponent] Feature non trovata nell'editor`, { id: event.id }); // prettier-ignore
            return;
          }
          editor.removeAt(editorIndex);

          const feature = featuresSource.current.getFeatureById(event.id);
          if (!feature) {
            console.warn(`[MapWidgetComponent] Feature non trovata nella mappa`, { id: event.id }); // prettier-ignore
            return;
          }
          featuresSource.current.removeFeature(feature);

          triggerIntersections();
          return;
        }
      }
    });

    return () => {
      listener.unsubscribe();
    };
  }, [
    editor,
    triggerIntersections,
    cadastralEditor,
    featuresSource,
    widget.selection?.multiple,
    t,
  ]);

  // Solamente in anteprima, quando la mappa è stata centrata e zoommata
  // nel pannello di configurazione, allora la mappa deve essere centrata
  useEffect(() => {
    if (!options.preview) return;
    if (widget.position?.center && widget.position?.zoom) {
      mapRef.current?.getView().setCenter(widget.position.center);
      mapRef.current?.getView().setZoom(widget.position.zoom);
    }
  }, [widget.position, options.preview]);

  const initMap = useCallback(
    (element: HTMLDivElement) => {
      console.log(`[MapWidgetComponent] initMap`, element);
      // Inizio a creare la mappa
      const map = new Map({
        target: element,
        layers: (widget.layers ?? []).map((layer, index) => {
          const l = creteMapLayerFromConfig(layer);
          l.set('id', `layer_${index}`);
          return l;
        }),
        view: new View({
          center: widget.position?.center ?? [
            11.189417912530622, 44.576276442981985,
          ],
          zoom: widget.position?.zoom ?? 4,
        }),
      });

      // Aggiungo il layer delle geometrie disegnate
      initDrawing(map);
      initIntersectionManager();

      interactions.current.draw!.on('drawend', async event => {
        mapSubject.current.next({ type: 'add', feature: event.feature });
      });
      interactions.current.modify!.on('modifyend', async event => {
        mapSubject.current.next({
          type: 'modify',
          feature: event.features.item(0),
        });
      });

      interactions.current.remove!.on('select', event => {
        if (event.selected.length === 0) return;
        mapSubject.current.next({ type: 'remove', feature: event.selected[0] });
      });

      mapRef.current = map;
      return map;
    },
    [
      widget.layers,
      widget.position?.center,
      widget.position?.zoom,
      initDrawing,
      initIntersectionManager,
      interactions,
    ],
  );

  const isMobile = useBreakpoint(down('lg'));

  return (
    <ConfigFormItem
      className="map-widget-component-form-item"
      name={options.name}
      label={<RichTextAdvancedRender text={options.label} />}
      help={<RichTextAdvancedRender text={options.helpText} />}
      layout={{
        hideVisualLabel: options.hideLabel,
      }}
    >
      <OpenLayersMap
        init={initMap}
        controls={
          <>
            <div
              style={{
                right: '0.5rem',
                top: '0.5rem',
                position: 'absolute',
                marginLeft: 40,
              }}
            >
              <Flex gap="0.5rem" wrap justify="flex-end">
                <MapSearchAddressControl map={mapRef} isMobile={isMobile} />
                {!options.readonly && (
                  <MapSearchCadastralControl
                    map={mapRef}
                    mapSubject={mapSubject}
                    isMobile={isMobile}
                  />
                )}
                {!options.readonly && (
                  <MapDrawingControls
                    onModeChange={handleModeChange}
                    mode={mode}
                    isMobile={isMobile}
                  />
                )}
                {!options.readonly && (
                  <MapUploadGeometriesControl mapSubject={mapSubject} />
                )}
                <div>
                  <Tooltip
                    title={t(
                      'map-widget.title',
                      'Centra mappa sulle geometrie',
                    )}
                  >
                    <Button
                      type="default"
                      style={{ borderWidth: '1px' }}
                      icon={<ColumnWidthOutlined aria-label="Centra" />}
                      onClick={() => {
                        if (featuresSource.current.getFeatures().length == 0) {
                          message.info(
                            t(
                              'map-widget.no-geometries',
                              'Nessuna geometria presente',
                            ),
                          );
                          return;
                        }

                        mapRef.current
                          ?.getView()
                          .fit(featuresSource.current.getExtent(), {
                            padding: [64, 64, 64, 64],
                          });
                      }}
                    />
                  </Tooltip>
                </div>
              </Flex>
            </div>
            <div
              style={{
                left: '0.5rem',
                bottom: '0.5rem',
                position: 'absolute',
              }}
            >
              <MapLayersToggleControl map={mapRef} widget={widget} />
            </div>
          </>
        }
      />
      {!widget.selection?.hideGeometriesTable && (
        <div>
          <DividerTitleElement
            title={t('map-widget.drawn-geometries', 'Geometrie disegnate')}
          />
          <MapGeometriesTable
            editor={editor}
            shape={widget.selection?.shape}
            readonly={options.readonly}
            onRemove={id =>
              mapSubject.current.next({
                type: 'removeId',
                id,
              })
            }
          />
        </div>
      )}
      {widget.intersection?.enabled && (
        <div>
          <DividerTitleElement
            title={
              widget.intersection?.title ??
              t('map-widget.intersections', 'Intersezioni')
            }
          />
          <MapCadastralAreasTable
            name={`${options.name}`}
            editor={cadastralEditor}
            readonly={options.readonly}
          />
        </div>
      )}
    </ConfigFormItem>
  );
}
